<script setup>
import { VisionAiButton as DashingVisionAiButton } from '@dashhudson/dashing-ui/vision-ai-button';
import { computed } from 'vue';

import { useIntentMap } from '@/components/VisionAi/composables/useIntentMap';
import {
  REGENERATE_TOOLTIPS,
  SUMMARY_INTENT_TYPES,
  VISION_AI_SUMMARY_LAUNCH_TYPE,
} from '@/components/VisionAi/constants';
import { aiSummaryLaunchEvent } from '@/components/VisionAi/utils/mixpanel-events';
import { useFlagStore } from '@/stores/flag';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';

const visionAiStore = useVisionAiStore();
const visionAiConversationsStore = useVisionAiConversationsStore();
const visionAiMessagesStore = useVisionAiMessagesStore();
const flagStore = useFlagStore();
const props = defineProps({
  large: {
    type: Boolean,
    default: false,
  },
  openAutoExpanded: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['openChatWindow']);

const { checkIfInvalidThreshold, getTooltip } = useIntentMap();

const tooltip = computed(() =>
  visionAiMessagesStore.summaryLoading
    ? REGENERATE_TOOLTIPS.LOADING
    : getTooltip(visionAiStore.currentIntentType),
);

async function handleButtonClicked(event) {
  visionAiStore.openChatWindow();
  if (flagStore.flags.visionAiStreaming && props.openAutoExpanded) {
    visionAiStore.expandChatPopup();
  }

  if (!visionAiConversationsStore.summariesSelected) {
    visionAiConversationsStore.selectSummaries();
    visionAiStore.clearLocalChatHistory();
    await visionAiMessagesStore.getMessages({ intentTypes: SUMMARY_INTENT_TYPES });
  }

  if (checkIfInvalidThreshold(visionAiStore.currentIntentType)) {
    await visionAiStore.createMockMessages({});
  } else {
    await visionAiStore.startConversation({
      message: visionAiStore.defaultPrompts[0],
    });
  }

  emit('openChatWindow', event);
  aiSummaryLaunchEvent({
    launchedFrom: VISION_AI_SUMMARY_LAUNCH_TYPE.ICON,
    intentType: visionAiStore.currentIntentType,
  });
}

const buttonSize = computed(() => (props.large ? 'lg' : 'sm'));
</script>

<template>
  <div data-cy="vision-ai-button" :class="['vision-ai-button', 'dash-transition-md', '!left-5']">
    <DashingVisionAiButton
      v-dash-tooltip="tooltip"
      :size="buttonSize"
      :loading="visionAiMessagesStore.summaryLoading"
      :disabled="visionAiMessagesStore.summaryLoading"
      severity="visionAI"
      label="Summarize"
      @click="handleButtonClicked($event)"
    />
  </div>
</template>
